export interface NextEnvVars {
  CHALK_DOCS_HOST?: string | null;
  CHALK_FRONTEND_HOST: string | null;
  CHALK_GRPC_API_HOST: string | null;
  CHALK_DEPLOYMENT_METHOD: string | null;
  CHALK_DEPLOYMENT_MODEL: string | null;
  CHALK_PLATFORM_VERSION: string | null;
  NEXT_PUBLIC_ALGOLIA_DASHBOARD_INDEX_NAME: string | null;
  NEXT_PUBLIC_ALGOLIA_DASHBOARD_APP_ID: string | null;
  NEXT_PUBLIC_AMPLITUDE_API_KEY: string | null;
  CUSTOM_AUTH_PROVIDERS: string | null;
  CUSTOMER_LIVERY_FILENAME: string | null;
  HELM_CUSTOM_AUTH_PROVIDERS: string | null;
  FEATURE_FLAG_OVERRIDES: string | null;
  DOPPLER_ENVIRONMENT: string | null;
  INTERNAL_KEYCLOAK_CLIENT_ID: string | null;
  NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY: string | null;
  SLACK_CLIENT_ID: string | null;
  SLACK_BOT_NAME: string | null;
  // Needed on the client so the no-organization page can offer the signup-code prompt only
  // where self-service signup actually exists.
  CHALK_PERMIT_SELF_SERVICE: string | null;
}

export function getNextEnvVars(): NextEnvVars {
  const env = process.env;

  // env alias here forces these env vars to actually be read from the nodejs environment
  return {
    CHALK_FRONTEND_HOST: env["CHALK_FRONTEND_HOST"] ?? null,
    CHALK_DOCS_HOST: env["CHALK_DOCS_HOST"] ?? null,
    CHALK_GRPC_API_HOST: env["CHALK_GRPC_API_HOST"] ?? null,
    CHALK_DEPLOYMENT_METHOD: env["CHALK_DEPLOYMENT_METHOD"] ?? null,
    CHALK_DEPLOYMENT_MODEL: env["CHALK_DEPLOYMENT_MODEL"] ?? null,
    CHALK_PLATFORM_VERSION: env["CHALK_PLATFORM_VERSION"] ?? "latest",
    NEXT_PUBLIC_ALGOLIA_DASHBOARD_INDEX_NAME:
      env["NEXT_PUBLIC_ALGOLIA_DASHBOARD_INDEX_NAME"] ?? null,
    NEXT_PUBLIC_ALGOLIA_DASHBOARD_APP_ID: env["NEXT_PUBLIC_ALGOLIA_DASHBOARD_APP_ID"] ?? null,
    NEXT_PUBLIC_AMPLITUDE_API_KEY: env["NEXT_PUBLIC_AMPLITUDE_API_KEY"] ?? null,
    CUSTOM_AUTH_PROVIDERS: env["CUSTOM_AUTH_PROVIDERS"] ?? null,
    CUSTOMER_LIVERY_FILENAME: env["CUSTOMER_LIVERY_FILENAME"] ?? null,
    HELM_CUSTOM_AUTH_PROVIDERS: env["HELM_CUSTOM_AUTH_PROVIDERS"] ?? null,
    FEATURE_FLAG_OVERRIDES: env["FEATURE_FLAG_OVERRIDES"] ?? null,
    DOPPLER_ENVIRONMENT: env["DOPPLER_ENVIRONMENT"] ?? null,
    INTERNAL_KEYCLOAK_CLIENT_ID: env["INTERNAL_KEYCLOAK_CLIENT_ID"] ?? null,
    NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY: env["NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY"]
      ? "TRUE"
      : "FALSE",
    SLACK_CLIENT_ID: env["SLACK_CLIENT_ID"] ?? null,
    SLACK_BOT_NAME: env["SLACK_BOT_NAME"] ?? null,
    CHALK_PERMIT_SELF_SERVICE: env["CHALK_PERMIT_SELF_SERVICE"] ?? null,
  };
}

/**
 * Do not call this from rendering code. `_app.tsx` applies it once and threads the result
 * into every page as the `envVars` prop, so the values a page already receives — and those
 * from the `useNextEnvVars` hook, which is the only way rendering code should reach them —
 * have their fallbacks resolved.
 *
 * Adding a var to `NextEnvVars` is not enough to make it readable client-side: it also has to
 * be listed in the `env` mapping in `next.config.js`, or it will be present under SSR and
 * `undefined` in a statically rendered build.
 */
export function getNextEnvVarFallbacks(envVars: NextEnvVars | undefined): NextEnvVars {
  return {
    CHALK_FRONTEND_HOST: envVars?.CHALK_FRONTEND_HOST ?? process.env.CHALK_FRONTEND_HOST ?? null,
    CHALK_DOCS_HOST: envVars?.CHALK_DOCS_HOST ?? process.env.CHALK_DOCS_HOST ?? null,
    CHALK_GRPC_API_HOST: envVars?.CHALK_GRPC_API_HOST ?? process.env.CHALK_GRPC_API_HOST ?? null,
    CHALK_DEPLOYMENT_METHOD:
      envVars?.CHALK_DEPLOYMENT_METHOD ?? process.env.CHALK_DEPLOYMENT_METHOD ?? null,
    CHALK_DEPLOYMENT_MODEL:
      envVars?.CHALK_DEPLOYMENT_MODEL ?? process.env.CHALK_DEPLOYMENT_MODEL ?? null,
    CHALK_PLATFORM_VERSION:
      envVars?.CHALK_PLATFORM_VERSION ?? process.env.CHALK_PLATFORM_VERSION ?? "latest",
    NEXT_PUBLIC_ALGOLIA_DASHBOARD_INDEX_NAME:
      envVars?.NEXT_PUBLIC_ALGOLIA_DASHBOARD_INDEX_NAME ??
      process.env.NEXT_PUBLIC_ALGOLIA_DASHBOARD_INDEX_NAME ??
      null,
    NEXT_PUBLIC_ALGOLIA_DASHBOARD_APP_ID:
      envVars?.NEXT_PUBLIC_ALGOLIA_DASHBOARD_APP_ID ??
      process.env.NEXT_PUBLIC_ALGOLIA_DASHBOARD_APP_ID ??
      null,
    NEXT_PUBLIC_AMPLITUDE_API_KEY:
      envVars?.NEXT_PUBLIC_AMPLITUDE_API_KEY ?? process.env.NEXT_PUBLIC_AMPLITUDE_API_KEY ?? null,
    CUSTOM_AUTH_PROVIDERS:
      envVars?.CUSTOM_AUTH_PROVIDERS ?? process.env.CUSTOM_AUTH_PROVIDERS ?? null,
    CUSTOMER_LIVERY_FILENAME:
      envVars?.CUSTOMER_LIVERY_FILENAME ?? process.env.CUSTOMER_LIVERY_FILENAME ?? null,
    HELM_CUSTOM_AUTH_PROVIDERS:
      envVars?.HELM_CUSTOM_AUTH_PROVIDERS ?? process.env.HELM_CUSTOM_AUTH_PROVIDERS ?? null,
    FEATURE_FLAG_OVERRIDES:
      envVars?.FEATURE_FLAG_OVERRIDES ?? process.env.FEATURE_FLAG_OVERRIDES ?? null,
    DOPPLER_ENVIRONMENT: envVars?.DOPPLER_ENVIRONMENT ?? process.env.DOPPLER_ENVIRONMENT ?? null,
    INTERNAL_KEYCLOAK_CLIENT_ID:
      envVars?.INTERNAL_KEYCLOAK_CLIENT_ID ?? process.env.INTERNAL_KEYCLOAK_CLIENT_ID ?? null,
    NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY:
      envVars?.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY ?? process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY
        ? "TRUE"
        : "FALSE",
    SLACK_CLIENT_ID: envVars?.SLACK_CLIENT_ID ?? process.env.SLACK_CLIENT_ID ?? null,
    SLACK_BOT_NAME: envVars?.SLACK_BOT_NAME ?? process.env.SLACK_BOT_NAME ?? null,
    CHALK_PERMIT_SELF_SERVICE:
      envVars?.CHALK_PERMIT_SELF_SERVICE ?? process.env.CHALK_PERMIT_SELF_SERVICE ?? null,
  };
}
